import { ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from '@tanstack/react-router';
import ConfirmButtons from '@zeus/core/buttons/ConfirmButtons';
import PermitSuperAdmin from '@zeus/core/PermitSuperAdmin';
import { useTopHeader } from '_/contexts/LayoutContext';
import {
  useExtractionCells,
  useExtractionCell,
  useCreateExtractionCell,
  useUpdateExtractionCell,
} from '_/hooks/useExtractionCells';
import { IExtractionCellForm } from '_/types/extractionCells/extractionCells.types';
import { EXTRACTION_CELL_INITIAL_VALUES, extractionCellValidationSchema } from '_/utils/extractionCellUtils';
import { Form, Input, Checkbox, Alert, Spin } from 'antd';
import React, { ReactElement, useEffect, useMemo, useCallback } from 'react';

const NewSystemExtractionCell = (): ReactElement => {
  const history = useNavigate();
  const [form] = Form.useForm<IExtractionCellForm>();
  const params = useParams({ strict: false }) as Partial<Record<string, string>>;
  const { extractionCellSlug } = params;
  const { setBreadcrumbs, setActionButtons } = useTopHeader();

  const isEditMode = !!extractionCellSlug;

  // Fetch extraction cells for validation
  const { data: extractionCells = [] } = useExtractionCells();

  // Fetch selected extraction cell if in edit mode
  const selectedCell = useMemo(() => {
    if (!extractionCellSlug || !extractionCells.length) return null;
    return extractionCells.find((cell) => cell.slug === extractionCellSlug);
  }, [extractionCellSlug, extractionCells]);

  const { data: selectedExtractionCell, isLoading: isLoadingCell } = useExtractionCell(
    selectedCell?.id || '',
    isEditMode && !!selectedCell?.id,
  );

  // Mutations
  const createMutation = useCreateExtractionCell();
  const updateMutation = useUpdateExtractionCell();

  const [formTouched, setFormTouched] = React.useState(false);

  const handleBackClick = useCallback(() => {
    void history({ to: '/extractioncells' });
  }, [history]);

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Extraction Cells', href: '/extractioncells' },
      { title: isEditMode ? 'Edit Extraction Cell' : 'New Extraction Cell' },
    ]);
    setActionButtons([
      {
        onClick: handleBackClick,
        icon: <ArrowLeftOutlined />,
        label: 'Back',
      },
    ]);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [setBreadcrumbs, setActionButtons, isEditMode, handleBackClick]);

  // Other extraction cells for validation (exclude current cell in edit mode)
  const otherExtractionCells = useMemo(
    () =>
      isEditMode && selectedExtractionCell ? extractionCells.filter((c) => c.id !== selectedExtractionCell.id) : extractionCells,
    [extractionCells, isEditMode, selectedExtractionCell],
  );

  // Initialize form values when editing
  useEffect(() => {
    if (isEditMode && selectedExtractionCell) {
      form.setFieldsValue({
        slug: selectedExtractionCell.slug,
        availabilityZone: selectedExtractionCell.availabilityZone,
        cellNumber: selectedExtractionCell.cellNumber,
        active: selectedExtractionCell.active,
      });
    } else if (!isEditMode) {
      form.setFieldsValue(EXTRACTION_CELL_INITIAL_VALUES);
    }
  }, [isEditMode, selectedExtractionCell, form]);

  const handleFieldChange = () => {
    setFormTouched(true);
  };

  const handleSubmit = async (values: IExtractionCellForm) => {
    try {
      if (isEditMode && selectedExtractionCell) {
        // Only send changed fields for update
        const updateData: any = {};
        if (selectedExtractionCell.availabilityZone !== values.availabilityZone) {
          updateData.availabilityZone = values.availabilityZone;
        }
        if (selectedExtractionCell.cellNumber !== values.cellNumber) {
          updateData.cellNumber = values.cellNumber;
        }
        if (selectedExtractionCell.active !== values.active) {
          updateData.active = values.active;
        }

        await updateMutation.mutateAsync({ id: selectedExtractionCell.id, data: updateData });
      } else {
        // Create new extraction cell
        await createMutation.mutateAsync(values);
      }
      setFormTouched(false);
      void history({ to: '/extractioncells' });
    } catch (error) {
      console.error('Error saving extraction cell:', error);
    }
  };

  // Custom validation function for Ant Design
  const validateField = async (fieldName: keyof IExtractionCellForm, value: any) => {
    try {
      const validationSchema = extractionCellValidationSchema(otherExtractionCells)();
      const fieldSchema = (validationSchema as any).fields[fieldName];
      if (fieldSchema) {
        await fieldSchema.validate(value);
      }
    } catch (err: any) {
      throw new Error(err.message);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveErrors = createMutation.error || updateMutation.error;

  // Show spinner while loading extraction cell data in edit mode
  if (isEditMode && isLoadingCell) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Show spinner while saving
  if (isSaving) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PermitSuperAdmin>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {saveErrors && (
          <Alert
            message={(saveErrors as any)?.messages?.[0] || 'An error occurred while saving'}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFieldsChange={handleFieldChange}
          initialValues={EXTRACTION_CELL_INITIAL_VALUES}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
            }}
          >
            <Form.Item
              label="Slug / ID - CANNOT BE CHANGED AFTER CREATION"
              name="slug"
              rules={
                !isEditMode
                  ? [
                      { required: true, message: 'Slug is required' },
                      {
                        validator: async (_, value) => {
                          if (value) {
                            await validateField('slug', value);
                          }
                        },
                      },
                    ]
                  : []
              }
              style={{ flex: 1 }}
            >
              <Input disabled={isEditMode} />
            </Form.Item>

            <Form.Item name="active" valuePropName="checked" style={{ marginTop: 30 }}>
              <Checkbox>Active</Checkbox>
            </Form.Item>
          </div>

          <Form.Item
            label="Availability Zone"
            name="availabilityZone"
            rules={[
              { required: true, message: 'Availability Zone is required' },
              {
                validator: async (_, value) => {
                  if (value) {
                    await validateField('availabilityZone', value);
                  }
                },
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Cell Number"
            name="cellNumber"
            rules={[
              { required: true, message: 'Cell Number is required' },
              {
                validator: async (_, value) => {
                  if (value) {
                    await validateField('cellNumber', value);
                  }
                },
              },
            ]}
          >
            <Input />
          </Form.Item>

          <ConfirmButtons confirmText="Save" disabled={!formTouched} onCancel={handleBackClick} onConfirm={() => form.submit()} />
        </Form>
      </div>
    </PermitSuperAdmin>
  );
};

export default NewSystemExtractionCell;
